import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api.js';
import StyleSettings from './EmbedStyleSettings.jsx';
import { ItemRow, Detail, Sections, Metrics, Glance, EditLayout, PreviewPane, FormPreview, SaveBar, moved, when } from './website/HubKit.jsx';

// Website contact forms — a "Contact us" form for the client's OWN website. The
// visitor picks an enquiry type, Pulse sends the message to that type's inbox(es)
// and keeps a copy (server/contactForms.js).
//
// Dual-surface via `scope`:
//   'admin-client' → Admin → the client → Contact forms
//   'my'           → Settings → Contact forms
//
// Mobile-first: one stacked column, ≥40px controls, tables scroll inside
// themselves rather than pushing the page sideways.
const BLANK = {
  name: '', heading: '', showHeading: true, blurb: '', buttonLabel: '', successMessage: '', privacyNotice: '', privacyUrl: '',
  labels: {}, topics: [{ key: 'general', label: 'General enquiries', to: [] }], showInbox: false, subjectPrefix: '',
  brandColor: '', font: { source: 'site' }, popup: { delay: 0, scroll: 0, exit: false, days: 7 }, allowedDomains: '', defaultLang: 'en', translations: {}, owlSiteId: '',
};
// The words on the form, with Pulse's default for each (server/contactForms.js UI.en).
const LABELS = [
  ['name', 'Name label', 'Name'], ['namePh', 'Name placeholder', 'Your name'],
  ['email', 'Email label', 'Email'], ['emailPh', 'Email placeholder', 'you@company.com'],
  ['topic', 'Enquiry type label', 'Enquiry type'], ['subtopic', 'Sub-topic label', 'What’s it about?'],
  ['message', 'Message label', 'Message'], ['messagePh', 'Message placeholder', 'How can we help? Tell us more about your enquiry.'],
  ['goesTo', 'Routing line', 'Your enquiry goes to'],
];
// Languages a form can be offered in — mirrors LANGS in server/newsletterI18n.js.
const LANGS = {
  en: 'English', it: 'Italian', fr: 'French', de: 'German', es: 'Spanish', pt: 'Portuguese', nl: 'Dutch', af: 'Afrikaans',
  zu: 'Zulu', xh: 'Xhosa', st: 'Sotho', tn: 'Tswana', sw: 'Swahili', ar: 'Arabic', pl: 'Polish', ro: 'Romanian', el: 'Greek',
  sv: 'Swedish', da: 'Danish', no: 'Norwegian', fi: 'Finnish', cs: 'Czech', hu: 'Hungarian', hr: 'Croatian', tr: 'Turkish',
  ru: 'Russian', uk: 'Ukrainian', he: 'Hebrew', hi: 'Hindi', ja: 'Japanese', ko: 'Korean', zh: 'Chinese',
};
const keyOf = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40);

export default function ContactFormsCard({ entityId, scope = 'my' }) {
  const apiScope = scope === 'my' ? 'my' : 'admin';
  const [forms, setForms] = useState(null);
  const [denied, setDenied] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  // Where we are: the list, one form (and which view), or a new form.
  const [view, setView] = useState({ mode: 'list', id: '', tab: 'overview' });
  const [draft, setDraft] = useState(BLANK);
  const [q, setQ] = useState('');
  // Ask the Owl first: null = the flag is off for this client (the controls stay hidden).
  const [owl, setOwl] = useState(null);
  useEffect(() => { api.contactOwl(entityId, apiScope).then(setOwl); }, [entityId, apiScope]);

  const load = useCallback(() => api.contactForms(entityId, apiScope)
    .then((v) => { if (v && v.error) throw new Error(v.error); setForms(v.forms || []); })
    .catch(() => setDenied(true)), [entityId, apiScope]);
  useEffect(() => { load(); }, [load]);

  if (denied) return null; // flag off / no permission — the section simply isn't there
  if (!forms) return <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>Loading contact forms…</div>;

  const run = async (key, fn) => {
    setBusy(key); setErr('');
    try {
      const v = await fn();
      if (v && v.error) { setErr(v.error); return false; }
      if (v && v.forms) setForms(v.forms);
      return v || true;
    } catch (e) { setErr(String(e.message || e)); return false; }
    finally { setBusy(''); }
  };
  const open = (f, tab = 'overview') => { setDraft(toDraft(f)); setErr(''); setView({ mode: 'item', id: f.id, tab }); };
  const startNew = () => { setDraft(BLANK); setErr(''); setView({ mode: 'new', id: '', tab: 'edit' }); };
  const back = () => { setErr(''); setView({ mode: 'list', id: '', tab: 'overview' }); };
  const save = async () => {
    const r = await run('save', () => (view.mode === 'new'
      ? api.contactFormCreate(entityId, apiScope, draft)
      : api.contactFormUpdate(entityId, apiScope, view.id, draft)));
    if (!r) return;
    const id = view.mode === 'new' ? r.id : view.id;
    const f = (r.forms || []).find((x) => x.id === id);
    if (f) open(f, 'overview');
  };

  // ── the list ───────────────────────────────────────────────────────────────
  if (view.mode === 'list') {
    const needle = q.trim().toLowerCase();
    const shown = forms.filter((f) => !needle || [f.name, ...f.topics.map((t) => `${t.label} ${(t.to || []).join(' ')}`)].join(' ').toLowerCase().includes(needle));
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <p style={{ ...hint, margin: 0 }}>
          “Contact us” forms for <b>your own website</b>. Each enquiry type goes to its own inbox, with <b>Reply</b> going straight
          back to the visitor, and every enquiry is also kept here.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {forms.length > 3 && <input type="search" style={{ ...input, flex: '1 1 240px' }} placeholder="Search forms, enquiry types or inboxes" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search contact forms" />}
          <button type="button" style={{ ...btn, ...primary }} onClick={startNew}>+ New contact form</button>
        </div>
        {forms.length === 0 && <div style={okBox}>No contact forms yet. Create one, set where each enquiry type goes, then add it to your website.</div>}
        {shown.map((f) => {
          const subs = f.topics.reduce((n, t) => n + (t.subs || []).length, 0);
          const inboxes = [...new Set(f.topics.flatMap((t) => t.to || []))];
          return (
            <ItemRow key={f.id} name={f.name} status={f.status === 'paused' ? '⏸ Paused' : '● Live'} tone={f.status === 'paused' ? 'paused' : 'live'}
              summary={`${f.topics.length} enquiry type${f.topics.length === 1 ? '' : 's'}${subs ? ` · ${subs} sub-topics` : ''}${(f.fields || []).length ? ` · ${(f.fields || []).length} extra question${f.fields.length === 1 ? '' : 's'}` : ''}${inboxes.length ? ` → ${inboxes.join(', ')}` : ''}`}
              stats={[{ value: f.total.toLocaleString(), label: 'enquiries' }, { value: f.lastWeek.toLocaleString(), label: 'this week' }, ...(f.failed ? [{ value: f.failed, label: 'not delivered', warn: true }] : []), { value: when(f.lastMessage), label: 'latest' }]}
              onOpen={() => open(f)} />
          );
        })}
        {err && <div style={errBox}>{err}</div>}
      </div>
    );
  }

  // ── one form (or a new one) ───────────────────────────────────────────────
  const f = forms.find((x) => x.id === view.id);
  const isNew = view.mode === 'new';
  if (!isNew && !f) { back(); return null; }
  const editor = (
    <EditLayout
      editor={(
        <>
          <Editor draft={draft} setDraft={setDraft} entityId={entityId} apiScope={apiScope} owl={owl} isNew={isNew} />
          <SaveBar onSave={save} onCancel={isNew ? back : () => open(f, 'overview')} busy={busy === 'save'} isNew={isNew} createLabel="Create form" error={err} />
        </>
      )}
      preview={<FormPreviewPane draft={draft} formId={isNew ? '' : f.id} entityId={entityId} apiScope={apiScope} />}
    />
  );
  if (isNew) {
    return <Detail backLabel="Contact forms" onBack={back} name={draft.name || 'New contact form'}>{editor}</Detail>;
  }
  return (
    <Detail backLabel="Contact forms" onBack={back} name={f.name} status={f.status === 'paused' ? '⏸ Paused' : '● Live'} tone={f.status === 'paused' ? 'paused' : 'live'}
      actions={[
        { label: f.status === 'paused' ? 'Resume' : 'Pause', disabled: !!busy, onClick: () => run(`st-${f.id}`, () => api.contactFormUpdate(entityId, apiScope, f.id, { status: f.status === 'paused' ? 'active' : 'paused' })) },
        { label: 'Delete', danger: true, disabled: !!busy, onClick: async () => { if (window.confirm(`Delete “${f.name}” and its ${f.total} enquir${f.total === 1 ? 'y' : 'ies'}? Download them first if you need them. Websites using this form will stop sending.`) && await run(`rm-${f.id}`, () => api.contactFormRemove(entityId, apiScope, f.id))) back(); } },
      ]}
      tabs={[['overview', 'Overview'], ['inbox', 'Enquiries'], ['code', 'Add to website'], ['edit', 'Edit']]}
      tab={view.tab} onTab={(tab) => { if (tab === 'edit') setDraft(toDraft(f)); setView({ ...view, tab }); }}>
      {view.tab === 'overview' && <Overview f={f} owl={owl} entityId={entityId} apiScope={apiScope} />}
      {view.tab === 'inbox' && <InboxPanel form={f} entityId={entityId} apiScope={apiScope} onChanged={load} />}
      {view.tab === 'code' && <CodePanel form={f} />}
      {view.tab === 'edit' && editor}
      {view.tab !== 'edit' && err && <div style={errBox}>{err}</div>}
    </Detail>
  );
}

// Saved form → the editor's draft. Saved enquiry types, sub-topics and
// questions keep their keys when renamed (links and translations use them).
function toDraft(f) {
  return { ...BLANK, ...f, allowedDomains: (f.allowedDomains || []).join(', '), fields: (f.fields || []).map((x) => ({ ...x, saved: true })), topics: (f.topics || []).map((t) => ({ ...t, saved: true, subs: (t.subs || []).map((x) => ({ ...x, saved: true })) })) };
}

// Overview: the numbers, what's set, then the form as the website shows it.
function Overview({ f, owl, entityId, apiScope }) {
  const fields = f.fields || [];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Metrics items={[
        { value: f.total.toLocaleString(), label: 'Enquiries', hint: 'Since the form went live' },
        { value: f.lastWeek.toLocaleString(), label: 'Last 7 days' },
        // Counted as deliveries, not failures: "0 · Not delivered" read as "none
        // delivered" (2026-10-26). The number people want is how many reached the
        // inbox; a shortfall is named beside it with where to fix it.
        { value: Math.max(0, f.total - (f.failed || 0)).toLocaleString(), label: 'Delivered', hint: f.failed ? `${f.failed} didn’t reach the inbox — send them again from Enquiries` : (f.total ? 'All reached their inbox' : 'No enquiries yet'), warn: f.failed > 0, good: !f.failed && f.total > 0 },
        { value: when(f.lastMessage), label: 'Latest enquiry' },
      ]} />
      <Glance rows={[
        ...f.topics.map((t) => [t.label, t.link ? `Opens ${hostOf(t.link)} ↗` : `${(t.subs || []).length ? `${t.subs.length} sub-topics → ` : '→ '}${(t.to || []).join(', ') || 'no inbox'}${owl && t.owl ? ' · Ask the Owl first' : ''}`]),
        ['Fields, in order', (f.layout || []).map((k) => ({ name: 'Name', email: 'Email', topic: 'Enquiry type', message: 'Message' }[k] || (fields.find((x) => x.key === k) || {}).label || k)).join(' · ')],
        ['Languages', [LANGS[f.defaultLang] || f.defaultLang, ...Object.keys(f.translations || {}).map((l) => LANGS[l] || l)].join(', ')],
        ['Popup', popupSummary(f.popup)],
        ['Only from these websites', (f.allowedDomains || []).join(', ') || 'Any website'],
        ['Each enquiry email', `From “<their name>”, subject “${f.subjectPrefix ? `${f.subjectPrefix} — ` : ''}<enquiry type> — <their name>”, Reply goes to them`],
      ]} />
      <FormPreviewPane draft={toDraft(f)} formId={f.id} entityId={entityId} apiScope={apiScope} />
    </div>
  );
}

function FormPreviewPane({ draft, formId, entityId, apiScope }) {
  const langs = [draft.defaultLang || 'en', ...Object.keys(draft.translations || {})];
  const [lang, setLang] = useState(langs[0]);
  return (
    <PreviewPane langs={langs.map((l) => [l, LANGS[l] || l])} lang={langs.includes(lang) ? lang : langs[0]} onLang={setLang}
      note="Drawn by the same code your website runs. Sending here shows the confirmation; nothing is sent.">
      <FormPreview kind="contact" entityId={entityId} apiScope={apiScope} form={draft} formId={formId} lang={langs.includes(lang) ? lang : langs[0]} />
    </PreviewPane>
  );
}

const popupSummary = (p = {}) => {
  const parts = [p.delay > 0 && `after ${p.delay}s`, p.scroll > 0 && `at ${p.scroll}% scroll`, p.exit && 'on exit'].filter(Boolean);
  return parts.length ? `Opens by itself ${parts.join(' or ')}` : 'Opens from your Contact button only';
};

// The editor: every setting, in collapsible sections that each say what's set.
function Editor({ draft, setDraft, isNew, entityId, apiScope, owl }) {
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const text = (k, lbl, ph, opts = {}) => (
    <label style={label}>{lbl}
      {opts.area
        ? <textarea style={{ ...input, minHeight: 64, resize: 'vertical' }} value={draft[k] || ''} placeholder={ph} maxLength={opts.max} onChange={(e) => set(k, e.target.value)} />
        : <input style={input} value={draft[k] || ''} placeholder={ph} maxLength={opts.max} onChange={(e) => set(k, e.target.value)} />}
    </label>
  );
  const topics = draft.topics || [];
  const fields = draft.fields || [];
  const sendable = topics.filter((t) => t.key && !isLink(t));
  const langs = Object.keys(draft.translations || {});
  const st = draft.style || {};
  return (
    <Sections initial={isNew ? ['basics', 'routing'] : []} list={[
      { id: 'basics', title: 'Name & heading', summary: `${draft.name || 'Untitled'} · ${draft.showHeading === false ? 'no heading' : `heading “${draft.heading || 'Contact us'}”`}`, content: (
        <>
          {text('name', 'Name (only you see this)', 'e.g. Contact page', { max: 120 })}
          <label style={{ ...label, ...chkRow }}><input type="checkbox" style={chk} checked={draft.showHeading !== false} onChange={(e) => set('showHeading', e.target.checked)} /> Show a heading (untick if your page already has its own title)</label>
          {draft.showHeading !== false && text('heading', 'Heading', 'Contact us', { max: 160 })}
          {text('blurb', 'Intro line (optional)', '', { area: true, max: 600 })}
        </>
      ) },
      { id: 'layout', title: 'Form layout', summary: layoutOf(draft).map((k) => partName(k, fields)).join(' · '), content: (
        <LayoutEditor draft={draft} set={set} owlFirst={!!owl && topics.some((t) => t.owl)} />
      ) },
      { id: 'routing', title: 'Enquiry types & inboxes', summary: `${topics.length} type${topics.length === 1 ? '' : 's'}${topics.some((t) => (t.subs || []).length) ? ` · ${topics.reduce((n, t) => n + (t.subs || []).length, 0)} sub-topics` : ''}${draft.subjectPrefix ? ` · subject “${draft.subjectPrefix} — …”` : ''}`, content: (
        <>
          <TopicsEditor topics={topics} onChange={(v) => set('topics', v)} owl={owl} />
          <label style={{ ...label, ...chkRow }}><input type="checkbox" style={chk} checked={!!draft.showInbox} onChange={(e) => set('showInbox', e.target.checked)} /> Show visitors which inbox their enquiry goes to (“Your enquiry goes to …”)</label>
          {text('subjectPrefix', 'Email subject starts with (optional)', 'Blank for nothing, or e.g. “KFF”', { max: 80 })}
          <div style={{ ...hint, marginTop: -4 }}>
            Each enquiry arrives as its own conversation: from the visitor’s name, with a subject like
            “{draft.subjectPrefix ? `${draft.subjectPrefix} — ` : ''}{topics[0]?.label || 'General enquiries'} — Michele Rossi”, and Reply goes straight to them.
            The sending address stays your verified one: no email service can send as the visitor’s own address.
          </div>
        </>
      ) },
      { id: 'questions', title: 'Extra questions', summary: fields.length ? fields.map((x) => x.label || '…').join(' · ') : 'None (name, email and message only)', content: (
        <FieldsEditor fields={fields} topics={sendable} onChange={(v) => set('fields', v)} />
      ) },
      { id: 'wording', title: 'Wording', summary: `${Object.values(draft.labels || {}).some(Boolean) || draft.buttonLabel || draft.successMessage ? 'Your own wording' : 'Default wording'} · messages up to ${(draft.messageMax || 5000).toLocaleString()} characters`, content: (
        <>
          <div style={subRow}>
            {LABELS.map(([k, lbl, ph]) => (
              <label key={k} style={{ ...label, marginTop: 6, fontWeight: 600 }}>{lbl}
                <input style={input} value={(draft.labels || {})[k] || ''} placeholder={ph} maxLength={200} onChange={(e) => set('labels', { ...(draft.labels || {}), [k]: e.target.value })} />
              </label>
            ))}
          </div>
          <label style={{ ...chkRow, ...label, fontWeight: 700, alignItems: 'center' }}>
            <span style={{ flex: '1 1 200px' }}>Message length limit</span>
            <input type="number" min={100} max={5000} step={50} inputMode="numeric" style={{ ...input, width: 110, flex: 'none' }} value={draft.messageMax ?? 5000}
              onChange={(e) => set('messageMax', e.target.value === '' ? '' : Number(e.target.value))} aria-label="Message length limit in characters" />
            <span style={{ fontWeight: 400, color: 'var(--muted)' }}>characters</span>
          </label>
          <div style={{ ...hint, marginTop: -4 }}>Between 100 and 5,000. Visitors see a counter under the message box, and it stops at the limit.</div>
          {text('buttonLabel', 'Button', 'Send enquiry', { max: 40 })}
          {text('successMessage', 'Confirmation after it sends', 'Email sent\nThanks for getting in touch. We’ll get back to you as soon as we can.', { area: true, max: 300 })}
          <div style={{ ...hint, marginTop: -4 }}>The first line shows in bold. The form clears once the message is sent.</div>
        </>
      ) },
      { id: 'privacy', title: 'Privacy', summary: draft.privacyNotice || draft.privacyUrl ? `Notice${draft.privacyUrl ? ' + policy link' : ''}` : 'No privacy notice', content: (
        <>
          {text('privacyNotice', 'Privacy notice (optional, shown above the button)', 'We’ll only use your details to answer your enquiry, in line with our {privacy}.', { area: true, max: 600 })}
          {text('privacyUrl', 'Privacy policy link', 'https://…', { max: 500 })}
        </>
      ) },
      { id: 'style', title: 'Style & font', summary: `${styleName(st)} · ${fontName(draft.font)}${draft.brandColor ? ` · button ${draft.brandColor}` : ''}`, content: (
        <>
          <label style={label}>Button colour
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="color" style={{ width: 48, height: 40, border: '1px solid var(--hairline)', borderRadius: 9, background: 'var(--bg)', padding: 2 }}
                value={/^#[0-9a-f]{6}$/i.test(draft.brandColor || '') ? draft.brandColor : '#111111'} onChange={(e) => set('brandColor', e.target.value)} />
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>{draft.brandColor ? draft.brandColor : 'Your branding colour'}</span>
              {draft.brandColor && <button type="button" style={{ ...btn, minHeight: 36 }} onClick={() => set('brandColor', '')}>Use branding</button>}
            </div>
          </label>
          <div style={label}>Look</div>
          <StyleSettings style={st} onChange={(v) => set('style', v)} onPreset={(p) => setDraft((d) => ({ ...d, style: p.style, brandColor: p.brandColor }))} />
          <div style={label}>Font</div>
          <FontSettings font={draft.font || {}} onChange={(v) => set('font', v)} />
        </>
      ) },
      { id: 'popup', title: 'Popup version', summary: popupSummary(draft.popup || BLANK.popup), content: (
        <PopupSettings popup={draft.popup || BLANK.popup} onChange={(v) => set('popup', v)} />
      ) },
      { id: 'languages', title: 'Languages', summary: [LANGS[draft.defaultLang || 'en'], ...langs.map((l) => LANGS[l] || l)].join(', '), content: (
        <LanguagesEditor draft={draft} setDraft={setDraft} entityId={entityId} apiScope={apiScope} />
      ) },
      owl && { id: 'owl', title: 'Ask the Owl first', summary: topics.some((t) => t.owl) ? `On for ${topics.filter((t) => t.owl).map((t) => t.label).join(', ')}` : 'Off', content: (
        <OwlSettings owl={owl} draft={draft} set={set} />
      ) },
      { id: 'security', title: 'Allowed websites', summary: draft.allowedDomains ? draft.allowedDomains : 'Any website', content: (
        <>
          {text('allowedDomains', 'Only accept enquiries from these websites (optional)', 'e.g. kappafuturfestival.it', { max: 1000 })}
          <div style={{ ...hint, marginTop: -4 }}>Leave blank to accept enquiries from any website. Each domain also covers its subdomains. The hosted link always works.</div>
        </>
      ) },
    ]} />
  );
}

// The order of the form's parts on the website (server/contactForms.js cleanLayout).
const layoutOf = (draft) => {
  const fields = (draft.fields || []).filter((x) => x.key || x.label);
  const keys = ['name', 'email', 'topic', ...fields.map((x) => x.key || keyOf(x.label)), 'message'];
  const out = (draft.layout || []).filter((k) => keys.includes(k));
  keys.forEach((k, i) => { if (!out.includes(k)) { const after = keys.slice(0, i).reverse().find((p) => out.includes(p)); out.splice(after ? out.indexOf(after) + 1 : 0, 0, k); } });
  return out;
};
const partName = (k, fields) => ({ name: 'Name', email: 'Email', topic: 'Enquiry type', message: 'Message' }[k] || (fields.find((x) => (x.key || keyOf(x.label)) === k) || {}).label || k);
function LayoutEditor({ draft, set, owlFirst }) {
  const order = layoutOf(draft);
  const fields = draft.fields || [];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 8 }}>
      {order.map((k, i) => (
        <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 6px 6px 12px', border: '1px solid var(--hairline)', borderRadius: 10, background: 'var(--bg)' }}>
          <span style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>{partName(k, fields)}</span>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>{['name', 'email', 'message'].includes(k) ? 'always asked' : k === 'topic' ? 'pills' : 'question'}</span>
          <button type="button" style={miniBtn} disabled={i === 0} onClick={() => set('layout', moved(order, i, -1))} aria-label={`Move ${partName(k, fields)} up`}>↑</button>
          <button type="button" style={miniBtn} disabled={i === order.length - 1} onClick={() => set('layout', moved(order, i, 1))} aria-label={`Move ${partName(k, fields)} down`}>↓</button>
        </div>
      ))}
      <div style={hint}>Move any part up or down: the preview follows. Add or remove questions under Extra questions.{owlFirst ? ' With “Ask the Owl first” on, the enquiry type always comes first on the website.' : ''}</div>
    </div>
  );
}
const styleName = (st = {}) => (st.labelStyle === 'caps' && st.buttonStyle === 'caps' ? 'Outlined, uppercase labels' : Object.keys(st).some((k) => !['labelStyle', 'fieldSize', 'pillStyle', 'buttonStyle'].includes(k) || !['normal', 'filled'].includes(st[k])) ? 'Custom look' : 'Pulse default look');
const fontName = (ft = {}) => ({ site: 'website’s font', system: 'plain font', google: `${ft.family || 'Google'} font`, file: `${ft.family || 'your'} font files` }[ft.source || 'site']);

// Each enquiry type: the label visitors pick, and the inbox(es) it is sent to.
// The key is fixed once saved (it's what ?enquiry=… links and translations use).
// "+ Add a question": ready-made fields first (they mean the same on every
// form), then custom ones. A custom field's key comes from its label once.
const FIELD_PRESETS = [
  { key: 'last_name', label: 'Surname', type: 'text' },
  { key: 'mobile', label: 'Mobile', type: 'tel' },
  { key: 'country', label: 'Country', type: 'country' },
  { key: 'company', label: 'Company', type: 'text' },
  { label: 'Short text', type: 'text', custom: true },
  { label: 'Number (e.g. how many people)', type: 'number', custom: true },
  { label: 'Choice (pick one option)', type: 'choice', custom: true },
  { label: 'Yes / no checkbox', type: 'checkbox', custom: true },
  { label: 'Date', type: 'date', custom: true },
  { label: 'Another email address', type: 'email', custom: true },
];
const TYPE_LABEL = { text: 'Text', email: 'Email', tel: 'Phone', number: 'Number', choice: 'Choice', checkbox: 'Yes / no', date: 'Date', country: 'Country list' };
function FieldsEditor({ fields, topics, onChange }) {
  const upd = (i, patch) => onChange(fields.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  const used = new Set(fields.map((f) => f.key));
  const add = (p) => {
    if (!p) return;
    onChange([...fields, p.custom
      ? { key: '', label: '', type: p.type, required: false, topics: [], ...(p.type === 'number' ? { min: 1, max: 20 } : {}), ...(p.type === 'choice' ? { options: [], display: 'pills' } : {}) }
      : { key: p.key, label: p.label, type: p.type, required: false, topics: [], saved: true }]);
  };
  return (
    <div>
      {fields.length === 0 && <div style={hint}>No extra questions. Add one below, e.g. a surname, the country, or “How many people?”.</div>}
      {fields.map((f, i) => (
        <div key={i} style={subRow}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <input style={{ ...input, flex: '1 1 180px' }} value={f.label} placeholder="The question, e.g. How many people?" maxLength={80} aria-label="Question"
              onChange={(e) => upd(i, { label: e.target.value, ...(f.saved ? {} : { key: keyOf(e.target.value) }) })} />
            <span style={pill}>{TYPE_LABEL[f.type] || f.type}</span>
          </div>
          {['text', 'email', 'tel', 'number'].includes(f.type) && (
            <input style={{ ...input, marginTop: 6 }} value={f.placeholder || ''} placeholder="Hint inside the box (optional)" maxLength={120} onChange={(e) => upd(i, { placeholder: e.target.value })} aria-label="Placeholder" />
          )}
          {f.type === 'number' && (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginTop: 6, fontSize: 13 }}>
              From <input type="number" style={{ ...input, width: 90 }} value={f.min ?? ''} onChange={(e) => upd(i, { min: e.target.value === '' ? '' : Number(e.target.value) })} aria-label="Lowest number" />
              to <input type="number" style={{ ...input, width: 90 }} value={f.max ?? ''} onChange={(e) => upd(i, { max: e.target.value === '' ? '' : Number(e.target.value) })} aria-label="Highest number" />
            </div>
          )}
          {f.type === 'choice' && (
            <>
              <textarea style={{ ...input, minHeight: 70, marginTop: 6 }} placeholder={'One option per line, e.g.\nGeneral admission\nVIP\nTable'} value={Array.isArray(f.options) ? f.options.join('\n') : f.options || ''}
                onChange={(e) => upd(i, { options: e.target.value.split('\n') })} aria-label="Options" />
              <select style={{ ...input, marginTop: 6 }} value={f.display || 'pills'} onChange={(e) => upd(i, { display: e.target.value })} aria-label="Shown as">
                <option value="pills">Shown as pills (best for a few options)</option>
                <option value="dropdown">Shown as a dropdown (best for a long list)</option>
              </select>
            </>
          )}
          {f.type === 'checkbox' && (
            <textarea style={{ ...input, minHeight: 50, marginTop: 6 }} value={f.text || ''} placeholder="Wording beside the box, e.g. I’d like a call back" maxLength={300} onChange={(e) => upd(i, { text: e.target.value })} aria-label="Checkbox wording" />
          )}
          {topics.length > 1 && (
            <div style={{ marginTop: 6 }}>
              <div style={{ ...hint, margin: '0 0 4px' }}>Ask it for: {(f.topics || []).length ? '' : <b>every enquiry type</b>}</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {topics.map((t) => {
                  const on = (f.topics || []).includes(t.key);
                  return <button key={t.key} type="button" style={{ ...miniBtn, ...(on ? { background: 'var(--brand, #111)', color: '#fff', borderColor: 'transparent' } : {}) }} aria-pressed={on}
                    onClick={() => upd(i, { topics: on ? f.topics.filter((k) => k !== t.key) : [...(f.topics || []), t.key] })}>{t.label}</button>;
                })}
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
            <label style={{ ...chkRow, minHeight: 36, margin: 0 }}><input type="checkbox" style={chk} checked={!!f.required} onChange={(e) => upd(i, { required: e.target.checked })} /> {f.type === 'checkbox' ? 'Must tick' : 'Required'}</label>
            <span style={{ flex: 1 }} />
            <button type="button" style={miniBtn} onClick={() => onChange(fields.filter((_, j) => j !== i))}>Remove</button>
          </div>
        </div>
      ))}
      {fields.length < 12 && (
        <select style={{ ...input, marginTop: 8 }} value="" onChange={(e) => add(FIELD_PRESETS[Number(e.target.value)])} aria-label="Add a question">
          <option value="">+ Add a question…</option>
          {FIELD_PRESETS.map((p, i) => (p.custom || !used.has(p.key)) && <option key={i} value={i}>{p.label}</option>)}
        </select>
      )}
      <div style={{ ...hint, marginTop: 6 }}>Answers appear in the enquiry email, the Enquiries list and the CSV. With several enquiry types you can ask a question for some of them only. Set where each question sits under Form layout.</div>
    </div>
  );
}

const isLink = (t) => t.mode === 'link' || (t.mode !== 'send' && !!t.link);
function TopicsEditor({ topics, onChange, owl }) {
  const upd = (i, patch) => onChange(topics.map((t, j) => (j === i ? { ...t, ...patch } : t)));
  const move = (i, d) => { const a = [...topics]; const [x] = a.splice(i, 1); a.splice(i + d, 0, x); onChange(a); };
  return (
    <div>
      {topics.map((t, i) => (
        <div key={i} style={subRow}>
          <input style={input} value={t.label} placeholder="What visitors pick, e.g. Media & Press" maxLength={80} aria-label="Enquiry type"
            onChange={(e) => upd(i, { label: e.target.value, ...(t.saved ? {} : { key: keyOf(e.target.value) }) })} />
          {/* Either it SENDS (to inboxes) or it OPENS a page, e.g. Work with us → the job application form. */}
          <select style={{ ...input, marginTop: 6 }} value={isLink(t) ? 'link' : 'send'} aria-label="What this enquiry type does"
            onChange={(e) => upd(i, e.target.value === 'link' ? { mode: 'link', link: t.link || 'https://' } : { mode: 'send', link: '' })}>
            <option value="send">Sends the message to an inbox</option>
            <option value="link">Opens a link in a new tab (no message sent)</option>
          </select>
          {isLink(t) ? (
            <>
              <input style={{ ...input, marginTop: 6 }} value={t.link || ''} placeholder="https://… e.g. your job application form" aria-label="Link" inputMode="url"
                onChange={(e) => upd(i, { link: e.target.value.trim() })} />
              <div style={{ ...hint, margin: '4px 0 0' }}>Shows as a pill with ↗. Tapping it opens this page in a new tab, and the visitor stays on your contact form.</div>
            </>
          ) : (
            <>
              <input style={{ ...input, marginTop: 6 }} value={Array.isArray(t.to) ? t.to.join(', ') : t.to || ''} placeholder="Send to, e.g. press@festival.com (up to 5, comma-separated)" aria-label="Inboxes"
                inputMode="email" onChange={(e) => upd(i, { to: e.target.value.split(/\s*,\s*/) })} />
              <SubsEditor topic={t} subs={t.subs || []} onChange={(v) => upd(i, { subs: v })} />
              {owl && (
                <div style={{ marginTop: 6 }}>
                  <label style={{ ...label, ...chkRow, marginTop: 0 }}><input type="checkbox" style={chk} checked={!!t.owl} onChange={(e) => upd(i, { owl: e.target.checked, ...(e.target.checked ? {} : { help: false }) })} /> Ask the Owl first: show “Ask a quick question” for this type</label>
                  {t.owl && <label style={{ ...label, ...chkRow, marginTop: 0, paddingLeft: 28 }}><input type="checkbox" style={chk} checked={!!t.help} onChange={(e) => upd(i, { help: e.target.checked })} /> Also answer ticketing questions from Howler’s help centre</label>}
                </div>
              )}
            </>
          )}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
            {t.key && !isLink(t) && <span style={{ ...hint, margin: 0 }}>Link key: <code>?enquiry={t.key}</code></span>}
            <span style={{ flex: 1 }} />
            <button type="button" style={miniBtn} disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move up">↑</button>
            <button type="button" style={miniBtn} disabled={i === topics.length - 1} onClick={() => move(i, 1)} aria-label="Move down">↓</button>
            <button type="button" style={miniBtn} disabled={topics.length === 1} onClick={() => onChange(topics.filter((_, j) => j !== i))}>Remove</button>
          </div>
        </div>
      ))}
      {topics.length < 12 && <button type="button" style={{ ...btn, marginTop: 8 }} onClick={() => onChange([...topics, { key: '', label: '', to: [], subs: [] }])}>+ Add enquiry type</button>}
      <div style={{ ...hint, marginTop: 6 }}>With only one type, visitors don’t have to choose. Inboxes are never shown on the site unless you tick the box below.</div>
    </div>
  );
}

// The optional second dropdown for one enquiry type ("Ticketing" → Refunds,
// Lost ticket…). With any here, visitors who pick this type must choose one.
// A sub-topic's inboxes are optional: blank sends to the type's inboxes.
function SubsEditor({ topic, subs, onChange }) {
  const [open, setOpen] = useState(subs.length > 0);
  const upd = (i, patch) => onChange(subs.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  if (!open) {
    return <button type="button" style={{ ...miniBtn, marginTop: 6 }} onClick={() => { setOpen(true); if (!subs.length) onChange([{ key: '', label: '', to: [] }]); }}>+ Add sub-topics (a second dropdown)</button>;
  }
  return (
    <div style={{ marginTop: 8, paddingLeft: 10, borderLeft: '2px solid var(--hairline)' }}>
      <div style={{ ...hint, margin: '0 0 4px' }}>
        Sub-topics for <b>{topic.label || 'this type'}</b>. Visitors who pick it then choose one of these. Leave a sub-topic’s inbox blank to send it to {topic.to && topic.to.filter(Boolean).length ? topic.to.filter(Boolean).join(', ') : 'the inbox above'}.
      </div>
      {subs.map((x, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 6 }}>
          <input style={{ ...input, flex: '1 1 160px' }} value={x.label} placeholder="e.g. Refunds" maxLength={80} aria-label="Sub-topic"
            onChange={(e) => upd(i, { label: e.target.value, ...(x.saved ? {} : { key: keyOf(e.target.value) }) })} />
          <input style={{ ...input, flex: '1 1 200px' }} value={Array.isArray(x.to) ? x.to.join(', ') : x.to || ''} placeholder="Own inbox (optional)" inputMode="email" aria-label="Sub-topic inboxes"
            onChange={(e) => upd(i, { to: e.target.value.split(/\s*,\s*/) })} />
          <button type="button" style={miniBtn} onClick={() => { const v = subs.filter((_, j) => j !== i); onChange(v); if (!v.length) setOpen(false); }} aria-label={`Remove ${x.label || 'sub-topic'}`}>Remove</button>
        </div>
      ))}
      {subs.length < 15 && <button type="button" style={{ ...miniBtn, marginTop: 6 }} onClick={() => onChange([...subs, { key: '', label: '', to: [] }])}>+ Add sub-topic</button>}
    </div>
  );
}

// Ask the Owl first (server/fanOwlContact.js): which Fan Owl site answers this
// form's questions. The Owl only uses the FAQs tagged with an enquiry type
// (Fan Owl → FAQs), so a type with nothing tagged hands straight to the team.
function OwlSettings({ owl, draft, set }) {
  const anyOn = (draft.topics || []).some((t) => t.owl);
  const site = (owl.sites || []).find((x) => x.id === draft.owlSiteId);
  return (
    <div style={subRow}>
      <b style={{ fontSize: 13 }}>Ask the Owl first</b>
      {!owl.on && <div style={{ ...hint, color: 'var(--error)' }}>Switched off for this client: turn on “Ask the Owl on the contact form” in Product → Flags. You can set it up now.</div>}
      <label style={{ ...label, marginTop: 6, fontWeight: 400 }}>Which Owl answers
        <select style={input} value={draft.owlSiteId || ''} onChange={(e) => set('owlSiteId', e.target.value)}>
          <option value="">— None (no Owl on this form) —</option>
          {(owl.sites || []).map((x) => <option key={x.id} value={x.id}>{x.name}{x.enabled ? '' : ' (switched off)'}</option>)}
        </select>
      </label>
      {!(owl.sites || []).length && <div style={hint}>Set up a Fan Owl site first (Fan Owl → Sites). It must be switched on, but it doesn’t need to be installed on your pages.</div>}
      {anyOn && !draft.owlSiteId && <div style={{ ...hint, color: 'var(--error)' }}>Pick an Owl, or the “Ask a quick question” button won’t show.</div>}
      {site && !site.enabled && <div style={{ ...hint, color: 'var(--error)' }}>That Owl is switched off, so the button won’t show until it’s on.</div>}
      <div style={hint}>The Owl answers only from FAQs tagged with that enquiry type (Fan Owl → FAQs → topic tags). If it can’t help, the visitor sends the form as usual, with the chat attached to the email.</div>
    </div>
  );
}

// Each translation is shown to visitors whose browser prefers that language, or
// when the site sets lang="…" on the form. Blank boxes use Pulse's wording in
// that language (English and Italian built in), else the main-language text.
// The form's font. "Your website's font" suits the one-line code (it sits inside
// the page); the hosted link and iframes can't see the website, so they need a
// Google Font or the font files themselves.
function FontSettings({ font, onChange }) {
  const source = font.source || 'site';
  const set = (k, v) => onChange({ ...font, source, [k]: v });
  return (
    <div style={subRow}>
      <select style={input} value={source} onChange={(e) => set('source', e.target.value)} aria-label="Font">
        <option value="site">Your website’s font (recommended with the code embed)</option>
        <option value="google">A Google Font</option>
        <option value="file">Your own font files</option>
        <option value="system">Plain system font</option>
      </select>
      {source === 'site' && <div style={{ ...hint, margin: '6px 0 0' }}>The form uses whatever font the page around it uses. The hosted link and iframes can’t see your website, so they show a plain font: pick a Google Font or your font files for those.</div>}
      {source === 'google' && (
        <>
          <input style={{ ...input, marginTop: 6 }} value={font.family || ''} placeholder="Font name exactly as on fonts.google.com, e.g. Inter" maxLength={60} onChange={(e) => set('family', e.target.value)} aria-label="Google Font name" />
          <div style={{ ...hint, margin: '4px 0 0' }}>Regular and bold are loaded from Google Fonts.</div>
        </>
      )}
      {source === 'file' && (
        <>
          <input style={{ ...input, marginTop: 6 }} value={font.family || ''} placeholder="Font name, e.g. Suisse" maxLength={60} onChange={(e) => set('family', e.target.value)} aria-label="Font name" />
          <input style={{ ...input, marginTop: 6 }} value={font.url || ''} placeholder="Regular font file: https://… .woff2, .woff, .otf or .ttf" inputMode="url" onChange={(e) => set('url', e.target.value.trim())} aria-label="Regular font file" />
          <input style={{ ...input, marginTop: 6 }} value={font.boldUrl || ''} placeholder="Bold font file (optional)" inputMode="url" onChange={(e) => set('boldUrl', e.target.value.trim())} aria-label="Bold font file" />
          <div style={{ ...hint, margin: '4px 0 0' }}>Use the font files your website already loads (ask your web team for the links). The font host must allow other websites to use them, and you need the licence to use the font on the web.</div>
        </>
      )}
    </div>
  );
}

// When the popup version opens by itself. All off = it only opens from the
// website's own "Contact us" button or link (the usual set-up).
function PopupSettings({ popup, onChange }) {
  const set = (k, v) => onChange({ ...popup, [k]: v });
  const num = (k, lbl, max, unit) => (
    <label style={{ ...chkRow, minHeight: 40, margin: '6px 0' }}>
      <span style={{ flex: '1 1 180px' }}>{lbl}</span>
      <input type="number" min={0} max={max} inputMode="numeric" style={{ ...input, width: 90, flex: 'none' }} value={popup[k] ?? 0}
        onChange={(e) => set(k, Math.max(0, Math.min(max, Number(e.target.value) || 0)))} aria-label={lbl} />
      <span style={{ width: 56, color: 'var(--muted)' }}>{unit}</span>
    </label>
  );
  const auto = popup.delay > 0 || popup.scroll > 0 || popup.exit;
  return (
    <div style={subRow}>
      <div style={{ ...hint, margin: 0 }}>Used when the form goes on the site as a popup (see “Add it to your website”). It always opens from your own “Contact us” button or link. Optionally it can also open by itself (0 = off):</div>
      {num('delay', 'Open after', 600, 'seconds')}
      {num('scroll', 'Open once they have scrolled', 100, '% of page')}
      <label style={{ ...chkRow, margin: '6px 0' }}><input type="checkbox" style={chk} checked={!!popup.exit} onChange={(e) => set('exit', e.target.checked)} /> Open when they move to leave the page (computers only)</label>
      {auto && num('days', 'After someone closes it, don’t open it by itself again for', 365, 'days')}
      {auto && <div style={{ ...hint, margin: 0 }}>Someone who has sent a message isn’t shown it automatically again.</div>}
    </div>
  );
}

function LanguagesEditor({ draft, setDraft, entityId, apiScope }) {
  const [open, setOpen] = useState('');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const tr = draft.translations || {};
  const main = draft.defaultLang || 'en';
  const setTr = (lang, v) => setDraft((d) => ({ ...d, translations: { ...(d.translations || {}), [lang]: v } }));
  const remove = (lang) => setDraft((d) => { const t = { ...(d.translations || {}) }; delete t[lang]; return { ...d, translations: t }; });
  // The Owl drafts every piece of wording — including the enquiry types and
  // sub-topics, which Pulse has no built-in words for — from the editor as it is.
  const translate = async (lang) => {
    const has = Object.values(tr[lang] || {}).some((v) => v && (typeof v !== 'object' || Object.keys(v).length));
    if (has && !window.confirm(`Replace the ${LANGS[lang]} wording with a fresh translation from the Owl?`)) return;
    setBusy(lang); setErr('');
    try {
      const r = await api.contactTranslate(entityId, apiScope, { to: lang, form: draft });
      if (r.error) setErr(r.error); else { setTr(lang, r.translation || {}); setOpen(lang); }
    } catch (e) { setErr(String(e.message || e)); }
    setBusy('');
  };
  // Names the client typed (enquiry types, sub-topics) stay in the main language
  // until they are translated — say how many, so it's not a surprise on the site.
  const untranslated = (lang) => {
    const t = tr[lang] || {};
    return (draft.topics || []).filter((x) => x.key).reduce((n, x) => n + ((t.topics || {})[x.key] ? 0 : 1)
      + (x.subs || []).filter((y) => y.key && !((t.subs || {})[x.key] || {})[y.key]).length, 0);
  };
  return (
    <div>
      <label style={{ ...label, marginTop: 0, fontWeight: 400 }}>Main language (the wording above is written in this)
        <select style={input} value={main} onChange={(e) => { const v = e.target.value; setDraft((d) => { const t = { ...(d.translations || {}) }; delete t[v]; return { ...d, defaultLang: v, translations: t }; }); }}>
          {Object.entries(LANGS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </label>
      {Object.keys(tr).map((lang) => (
        <div key={lang} style={subRow}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <b style={{ flex: '1 1 120px' }}>{LANGS[lang] || lang}</b>
            <button type="button" style={miniBtn} disabled={!!busy} onClick={() => translate(lang)}>{busy === lang ? 'Translating…' : 'Translate with the Owl'}</button>
            <button type="button" style={miniBtn} onClick={() => setOpen(open === lang ? '' : lang)}>{open === lang ? 'Close' : 'Edit wording'}</button>
            <button type="button" style={miniBtn} onClick={() => remove(lang)}>Remove</button>
          </div>
          {untranslated(lang) > 0 && <div style={{ ...hint, margin: '6px 0 0', color: 'var(--error)' }}>{untranslated(lang)} enquiry type or sub-topic name{untranslated(lang) === 1 ? '' : 's'} still show{untranslated(lang) === 1 ? 's' : ''} in {LANGS[main]}. Translate with the Owl, or type them under Edit wording.</div>}
          {open === lang && <TranslationFields draft={draft} t={tr[lang] || {}} onChange={(v) => setTr(lang, v)} />}
        </div>
      ))}
      <select style={{ ...input, marginTop: 8 }} value="" disabled={!!busy} onChange={(e) => { const l = e.target.value; if (l) { setTr(l, {}); translate(l); } }} aria-label="Add a language">
        <option value="">{busy ? 'Translating…' : '+ Add a language (the Owl translates it for you to check)…'}</option>
        {Object.entries(LANGS).filter(([k]) => k !== main && !tr[k]).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      {err && <div style={errBox}>{err}</div>}
    </div>
  );
}

function TranslationFields({ draft, t, onChange }) {
  const set = (k, v) => onChange({ ...t, [k]: v });
  const box = (k, lbl, ph, area) => (
    <label key={k} style={{ ...label, fontWeight: 600 }}>{lbl}
      {area ? <textarea style={{ ...input, minHeight: 56 }} value={t[k] || ''} placeholder={ph || ''} onChange={(e) => set(k, e.target.value)} />
        : <input style={input} value={t[k] || ''} placeholder={ph || ''} onChange={(e) => set(k, e.target.value)} />}
    </label>
  );
  return (
    <div>
      <div style={{ ...hint, marginTop: 6 }}>The grey text is your main-language wording. English and Italian have Pulse’s own wording built in, so blank boxes are fine there.</div>
      {draft.showHeading !== false && box('heading', 'Heading', draft.heading)}
      {box('blurb', 'Intro line', draft.blurb, true)}
      {LABELS.map(([k, lbl, ph]) => box(k, lbl, (draft.labels || {})[k] || ph))}
      {(draft.topics || []).filter((x) => x.key).map((x) => (
        <div key={x.key}>
          <label style={{ ...label, fontWeight: 600 }}>Enquiry type: {x.label}
            <input style={input} value={(t.topics || {})[x.key] || ''} placeholder={x.label} onChange={(e) => onChange({ ...t, topics: { ...(t.topics || {}), [x.key]: e.target.value } })} />
          </label>
          {(x.subs || []).filter((y) => y.key).map((y) => (
            <label key={y.key} style={{ ...label, fontWeight: 600, paddingLeft: 12 }}>{x.label} › {y.label}
              <input style={input} value={((t.subs || {})[x.key] || {})[y.key] || ''} placeholder={y.label}
                onChange={(e) => onChange({ ...t, subs: { ...(t.subs || {}), [x.key]: { ...((t.subs || {})[x.key] || {}), [y.key]: e.target.value } } })} />
            </label>
          ))}
        </div>
      ))}
      {(draft.fields || []).filter((x) => x.key && x.type !== 'country').map((x) => {
        const fv = (t.fields || {})[x.key] || {};
        const setF = (patch) => onChange({ ...t, fields: { ...(t.fields || {}), [x.key]: { ...fv, ...patch } } });
        return (
          <div key={x.key}>
            <label style={{ ...label, fontWeight: 600 }}>Question: {x.label}
              <input style={input} value={fv.label || ''} placeholder={x.label} onChange={(e) => setF({ label: e.target.value })} />
            </label>
            {x.placeholder && <label style={{ ...label, fontWeight: 600, paddingLeft: 12 }}>Hint
              <input style={input} value={fv.placeholder || ''} placeholder={x.placeholder} onChange={(e) => setF({ placeholder: e.target.value })} /></label>}
            {x.type === 'checkbox' && x.text && <label style={{ ...label, fontWeight: 600, paddingLeft: 12 }}>Wording beside the box
              <textarea style={{ ...input, minHeight: 50 }} value={fv.text || ''} placeholder={x.text} onChange={(e) => setF({ text: e.target.value })} /></label>}
            {x.type === 'choice' && <label style={{ ...label, fontWeight: 600, paddingLeft: 12 }}>Options, one per line, same order
              <textarea style={{ ...input, minHeight: 60 }} value={(fv.options || []).join('\n')} placeholder={(x.options || []).join('\n')} onChange={(e) => setF({ options: e.target.value.split('\n') })} /></label>}
          </div>
        );
      })}
      {box('buttonLabel', 'Button', draft.buttonLabel)}
      {box('successMessage', 'Confirmation after it sends', draft.successMessage, true)}
      {box('privacyNotice', 'Privacy notice', draft.privacyNotice, true)}
    </div>
  );
}

function CodePanel({ form }) {
  const [copied, setCopied] = useState('');
  const copy = (k, v) => { try { navigator.clipboard.writeText(v); setCopied(k); setTimeout(() => setCopied(''), 1500); } catch { /* select manually */ } };
  const popupSnippet = `${form.embedSnippet.replace('></howler-contact>', ' popup></howler-contact>')}\n<a href="#" data-howler-contact-open>Contact us</a>`;
  const frameHelper = `<script src="${form.hostedUrl.replace(/\/ct\/.*$/, '')}/howler-contact-frame.js" defer></script>`;
  const iframe = `<iframe src="${form.hostedUrl}?embed=1" style="width:100%;max-width:560px;height:820px;border:0" title="Contact form"></iframe>`;
  return (
    <div style={panel}>
      <div style={label}>1 · Paste this where the form should appear (recommended)</div>
      <pre style={code}>{form.embedSnippet}</pre>
      <button type="button" style={btn} onClick={() => copy('embed', form.embedSnippet)}>{copied === 'embed' ? '✓ Copied' : 'Copy code'}</button>
      <div style={{ ...hint, marginTop: 6 }}>
        Works on any site that lets you add HTML. Add <code>theme="dark"</code> for dark backgrounds. It follows your website’s
        language by itself (the page’s language setting, like the lineup embed), and switching language keeps what the visitor typed.
        To fix one language instead, add <code>lang="it"</code>.
        Link straight to an enquiry type with <code>?enquiry=…</code> on the page address, e.g. <code>?enquiry={form.topics[0]?.key}</code>,
        and to a sub-topic too with <code>&amp;topic=…</code> (each one’s key is its name in lower case with underscores, e.g. <code>lost_ticket</code>).
      </div>

      <div style={label}>Or as a popup</div>
      <pre style={code}>{popupSnippet}</pre>
      <button type="button" style={btn} onClick={() => copy('popup', popupSnippet)}>{copied === 'popup' ? '✓ Copied' : 'Copy popup code'}</button>
      <div style={{ ...hint, marginTop: 6 }}>
        The form opens over the page when someone taps any button or link with <code>data-howler-contact-open</code>, e.g. your
        menu’s “Contact” item. On a phone it slides up from the bottom. {popupWhen(form.popup)}
      </div>

      <div style={label}>2 · Or share a link</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input style={{ ...input, flex: '1 1 220px' }} readOnly value={form.hostedUrl} onFocus={(e) => e.target.select()} />
        <button type="button" style={btn} onClick={() => copy('link', form.hostedUrl)}>{copied === 'link' ? '✓ Copied' : 'Copy link'}</button>
        <a style={{ ...btn, textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }} href={form.hostedUrl} target="_blank" rel="noopener noreferrer">Open ↗</a>
      </div>
      <div style={{ ...hint, marginTop: 6 }}>
        Site builder only allows an iframe? Use this. Add the helper script below once on the page and the iframe grows and shrinks
        with the form and follows your website’s language. (It can’t use your website’s font: pick a Google Font or your font files.)
      </div>
      <pre style={code}>{iframe}</pre>
      <button type="button" style={btn} onClick={() => copy('iframe', iframe)}>{copied === 'iframe' ? '✓ Copied' : 'Copy iframe'}</button>
      <pre style={{ ...code, marginTop: 8 }}>{frameHelper}</pre>
      <button type="button" style={btn} onClick={() => copy('helper', frameHelper)}>{copied === 'helper' ? '✓ Copied' : 'Copy helper script'}</button>

      <details style={{ marginTop: 12 }}>
        <summary style={{ fontSize: 12.5, cursor: 'pointer', minHeight: 32 }}>3 · For developers: build your own form</summary>
        <div style={{ ...hint, marginTop: 6 }}>
          POST JSON to <code>{form.apiUrl}</code> with <code>{'{ name, email, topic, subtopic?, message, lang?, page? }'}</code> (<code>subtopic</code> is required for a type that has sub-topics), where <code>topic</code> is
          one of: {form.topics.map((t) => <code key={t.key} style={{ marginRight: 4 }}>{t.key}</code>)}. It returns <code>{'{ ok: true, message }'}</code> or
          a 4xx/5xx with a readable <code>error</code>. GET the same URL for the form’s wording. Leave a hidden <code>website</code> field empty; bots that fill it in are ignored.
        </div>
      </details>

      <div style={label}>Preview</div>
      <iframe title="Contact form preview" src={form.hostedUrl} style={{ width: '100%', maxWidth: 560, height: 820, border: '1px solid var(--hairline)', borderRadius: 12, background: '#fff' }} />
      <div style={{ ...hint, marginTop: 4 }}>Heads up: this preview is live, so anything you send here really goes to the inbox.</div>
    </div>
  );
}

function InboxPanel({ form, entityId, apiScope, onChanged }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [openId, setOpenId] = useState('');
  useEffect(() => {
    const t = setTimeout(() => {
      api.contactMessages(entityId, apiScope, form.id, q).then((v) => setRows(v.messages || [])).catch((e) => setErr(String(e.message || e)));
    }, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [entityId, apiScope, form.id, q]);
  const act = async (id, fn) => {
    setBusy(id); setErr('');
    try { const v = await fn(); if (v.error) setErr(v.error); else { setRows(v.messages || []); onChanged(); } } catch (e) { setErr(String(e.message || e)); }
    setBusy('');
  };
  return (
    <div style={panel}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input style={{ ...input, flex: '1 1 200px' }} placeholder="Search name, email or message" value={q} onChange={(e) => setQ(e.target.value)} />
        <a style={{ ...btn, textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }} href={api.contactMessagesCsvUrl(entityId, apiScope, form.id)}>⬇ Download CSV</a>
      </div>
      {err && <div style={errBox}>{err}</div>}
      {!rows ? <div style={{ ...hint, marginTop: 8 }}>Loading…</div> : rows.length === 0 ? (
        <div style={{ ...hint, marginTop: 8 }}>{q ? 'Nothing matches that search.' : 'No enquiries yet. Once the form is on your website, every message appears here as well as in the inbox.'}</div>
      ) : (
        <div style={{ marginTop: 8 }}>
          {rows.map((m) => (
            <div key={m.id} style={subRow}>
              <button type="button" onClick={() => setOpenId(openId === m.id ? '' : m.id)} style={{ all: 'unset', cursor: 'pointer', display: 'block', width: '100%', minHeight: 40 }}>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'baseline' }}>
                  <b style={{ fontSize: 13.5 }}>{m.name}</b>
                  <span style={{ fontSize: 12, color: 'var(--muted)' }}>{m.email}</span>
                  <span style={{ flex: 1 }} />
                  <span style={{ fontSize: 12, color: 'var(--muted)' }}>{when(m.createdAt)}</span>
                </div>
                <div style={{ fontSize: 12.5, marginTop: 2 }}>
                  <span style={pill}>{m.subtopic ? `${m.topic} · ${m.subtopic}` : m.topic}</span>{' '}
                  {m.status === 'sent' ? <span style={{ color: 'var(--muted)' }}>✓ sent to {m.sentTo.join(', ')}</span>
                    : <span style={{ color: 'var(--error)' }}>✕ not delivered{m.error ? ` (${m.error})` : ''}</span>}
                </div>
                {openId !== m.id && <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.message}</div>}
              </button>
              {openId === m.id && (
                <div>
                  <div style={{ fontSize: 13.5, whiteSpace: 'pre-wrap', lineHeight: 1.5, margin: '8px 0', overflowWrap: 'anywhere' }}>{m.message}</div>
                  {(m.answers || []).length > 0 && (
                    <div style={{ fontSize: 13, lineHeight: 1.6, margin: '0 0 8px' }}>
                      {m.answers.map((a) => <div key={a.key}><span style={{ color: 'var(--muted)' }}>{a.label}:</span> {a.value}</div>)}
                    </div>
                  )}
                  {m.page && <div style={{ ...hint, margin: 0, overflowWrap: 'anywhere' }}>From {m.page}{m.lang ? ` · ${LANGS[m.lang] || m.lang}` : ''}</div>}
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                    <a style={{ ...miniBtn, textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }} href={`mailto:${m.email}?subject=${encodeURIComponent(`Re: ${m.subtopic ? `${m.topic} · ${m.subtopic}` : m.topic}`)}`}>Reply</a>
                    {m.status !== 'sent' && <button type="button" style={miniBtn} disabled={!!busy} onClick={() => act(m.id, () => api.contactMessageResend(entityId, apiScope, form.id, m.id))}>{busy === m.id ? 'Sending…' : 'Send again'}</button>}
                    <button type="button" style={miniBtn} disabled={!!busy} onClick={() => { if (window.confirm('Delete this enquiry from Pulse? (Copies already in the inbox are not affected.)')) act(m.id, () => api.contactMessageRemove(entityId, apiScope, form.id, m.id)); }}>Delete</button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {rows.length >= 500 && <div style={{ ...hint, marginTop: 6 }}>Showing the newest 500. Download the CSV for everything.</div>}
        </div>
      )}
    </div>
  );
}

const popupWhen = (p = {}) => {
  const parts = [p.delay > 0 && `after ${p.delay} seconds`, p.scroll > 0 && `once ${p.scroll}% of the page is scrolled`, p.exit && 'when they move to leave'].filter(Boolean);
  return parts.length ? `It also opens by itself ${parts.join(', or ')} (change this under Edit → Popup version).` : 'It doesn’t open by itself (you can switch that on under Edit → Popup version).';
};
const hostOf = (u) => { try { return new URL(u).hostname; } catch { return u; } };

const hint = { fontSize: 12, color: 'var(--muted)', lineHeight: 1.5, margin: '0 0 8px' };
const label = { display: 'block', fontSize: 12, fontWeight: 700, margin: '12px 0 4px' };
const chkRow = { display: 'flex', gap: 8, alignItems: 'flex-start', fontWeight: 400, fontSize: 13, lineHeight: 1.45, minHeight: 32 };
const chk = { width: 18, height: 18, marginTop: 1, flex: 'none' };
const input = { width: '100%', boxSizing: 'border-box', minHeight: 40, padding: '9px 10px', borderRadius: 9, border: '1px solid var(--hairline)', background: 'var(--bg)', color: 'inherit', fontSize: 14, fontFamily: 'inherit' };
const btn = { minHeight: 40, padding: '9px 14px', borderRadius: 9, border: '1px solid var(--hairline)', background: 'var(--bg)', color: 'inherit', fontSize: 13, cursor: 'pointer', boxSizing: 'border-box' };
const primary = { background: 'var(--brand, #111)', color: '#fff', borderColor: 'transparent', fontWeight: 700 };
const panel = { marginTop: 12, paddingTop: 4, borderTop: '1px solid var(--hairline)' };
const pill = { fontSize: 11, padding: '2px 8px', borderRadius: 999, background: 'var(--bg)', border: '1px solid var(--hairline)', color: 'var(--muted)' };
const subRow = { border: '1px solid var(--hairline)', borderRadius: 9, padding: 8, marginTop: 6, background: 'var(--bg)' };
const miniBtn = { ...btn, minHeight: 36, padding: '6px 10px' };
const code = { fontSize: 12, background: 'var(--bg)', border: '1px solid var(--hairline)', borderRadius: 9, padding: 10, whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: '0 0 8px' };
const errBox = { fontSize: 12.5, color: 'var(--error)', marginTop: 8, lineHeight: 1.5 };
const okBox = { fontSize: 12.5, marginTop: 10, padding: 10, borderRadius: 9, background: 'var(--bg)', border: '1px solid var(--hairline)', lineHeight: 1.5 };
